import './focus-layer.scss';
import { Video } from './../../components/video/video';

/**
 * Manages the “Focus Layer” interactive module:
 * - Lazy-loads videos when the module enters the viewport.
 * - Synchronizes layer visibility/focus with toggles and hotspots.
 * - Coordinates playback with a media-control custom event and viewport re-entry.
 */
class FocusLayer {
    constructor (element, options) {
        // Defaults & Settings
        const defaults = {
            initAttr: 'data-focuslayer',
            indexAttr: 'data-focuslayer-index',
            activeAttr: 'data-focuslayer-active',
            pausedAttr: 'data-focuslayer-paused',
            focusedAttr: 'data-focuslayer-focused',
            inactiveAttr: 'data-focuslayer-inactive',
            hiddenAttr: 'data-focuslayer-hidden',
            toggleAttr: 'data-focuslayer-toggle',
            hotspotAttr: 'data-focuslayer-hotspot',
            videoValue: 'video',
            additonalVideoValue: 'additional-video',
            mediaValue: 'media',
            contentValue: 'content',
            boxValue: 'box',
            layerContentValue: 'with-content',
            layerEmptyValue: 'no-content',
            mediaControlValue: 'media-control'
        };

        this.settings = Object.assign({}, defaults, options);

        // Elements
        this.$root = element;
        this.$$layers = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.layerContentValue}"], [${this.settings.initAttr}="${this.settings.layerEmptyValue}"]`);
        this.$$videos = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.videoValue}"], [${this.settings.initAttr}="${this.settings.additonalVideoValue}"]`);
        this.$$toggles = this.$root.querySelectorAll(`[${this.settings.toggleAttr}]`);
        this.$$hotspots = this.$root.querySelectorAll(`[${this.settings.hotspotAttr}]`);
        this.$$boxes = this.$root.querySelectorAll(`[${this.settings.initAttr}="${this.settings.boxValue}"]`);
        this.$mediaValue = this.$root.querySelector(`[${this.settings.initAttr}="${this.settings.mediaValue}"]`);

        // Properties
        this.activeIndex = -1;
        this.animationType = 'initial';
        this.paused = false;
    }

    /**
     * Initializes the module if layer elements exist.
     * This sets up lazy video loading, user interactions, and viewport replay behavior.
     *
     * @returns {void} Does not return a value.
     */
    initialize () {
        if (this.$$layers.length > 0) {
            this.prepareVideos();
            this.initializeInteractions();
            this.replayInView();
        }
    }

    /**
     * Prepares video elements for lazy loading:
     * - Waits until the module is near the viewport.
     * - Loads visible layer videos first.
     * - After visible videos have loaded enough data, loads remaining hidden-layer videos.
     *
     * @returns {void} Does not return a value.
     */
    prepareVideos () {
        let videosShouldLoad = false;

        const loadVideos = () => {
            const $$hiddenVideos = [];

            this.$$layers.forEach($layer => {
                const $video = $layer.querySelector(`[${this.settings.initAttr}="${this.settings.videoValue}"]`);
                if ($video) {
                    const isVisible = $layer.getAttribute(this.settings.inactiveAttr) === 'false';

                    // Attach a small API wrapper per video so loading/playback logic can be centralized.
                    const videoAPI = new Video($video);
                    $video.videoAPI = videoAPI;

                    if (isVisible) {
                        // Load visible videos immediately and start/stop them according to current state.
                        $video.videoAPI.loadVideo(() => {
                            this.toggleVideoState(null, $video, true);
                        });
                    } else {
                        // Defer loading hidden videos until visible ones are ready (reduces contention).
                        $$hiddenVideos.push($video);
                    }
                }
            });

            // Wait until all currently visible-layer videos have buffered enough data (readyState >= 2).
            Promise.all(
                Array.from(this.$$layers)
                    .filter($layer => $layer.getAttribute(this.settings.inactiveAttr) === 'false')
                    .map($layer => {
                        const $video = $layer.querySelector(`[${this.settings.initAttr}="${this.settings.videoValue}"]`);
                        return new Promise(resolve => {
                            if (!$video) return resolve();
                            if ($video.readyState >= 2) {
                                resolve();
                            } else {
                                $video.addEventListener('loadeddata', resolve, { once: true });
                            }
                        });
                    })
            ).then(() => {
                // Once visible videos are ready, load remaining hidden videos without prioritizing playback.
                $$hiddenVideos.forEach($hiddenVideo => {
                    const videoAPI = new Video($hiddenVideo);
                    $hiddenVideo.videoAPI = videoAPI;
                    $hiddenVideo.videoAPI.loadVideo(() => {
                        this.toggleVideoState(null, $hiddenVideo, false);
                    });
                });
            });
        };

        const observeVideos = () => {
            if (!this.$root) return;

            // Use an IntersectionObserver to avoid network/decoding work until the module is near view.
            const observer = new IntersectionObserver(
                (entries, observerInstance) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting) {
                            videosShouldLoad = true;

                            // Disconnect after first trigger to ensure videos only load once.
                            if (videosShouldLoad) {
                                loadVideos();
                                observerInstance.disconnect();
                            }
                        }
                    });
                },
                {
                    root: null,
                    rootMargin: '50% 0px',
                    threshold: 0
                }
            );

            observer.observe(this.$root);
        };

        observeVideos();
    }

    /**
     * Updates module and layer attributes to represent a specific animation/focus state.
     * The `type` is expected to be one of:
     * - `initial`
     * - `focus-layers`
     * - `center-focus-layer-{index}`
     * - `focus-layer-{index}`
     *
     * @param {string} type - Animation type identifier controlling which layers are shown/focused.
     * @returns {void} Does not return a value.
     */
    toggleAnimationState (type) {
        this.animationType = type;
        const isActive = type !== 'initial';
        const lastLayerIndex = this.$$layers.length - 1;

        // Mark the module active/inactive and lock/unlock hotspots as needed.
        this.$root.toggleAttribute(this.settings.activeAttr, isActive);
        this.$$hotspots.forEach($hotspot => {
            $hotspot.toggleAttribute('disabled', isActive);
        });

        // The switch is written as `switch(true)` to allow boolean expressions per case.
        switch (true) {
        case type === 'initial': {
            this.$$boxes.forEach($box => $box.removeAttribute(this.settings.hiddenAttr));

            this.$$layers.forEach(($layer, index) => {
                const isLast = index === lastLayerIndex;

                // Reset focus/visibility and keep only the last layer active by default.
                $layer.removeAttribute(this.settings.focusedAttr);
                $layer.removeAttribute(this.settings.hiddenAttr);
                $layer.setAttribute(this.settings.inactiveAttr, !isLast);
                this.toggleVideoState($layer, null, isLast);
            });
            break;
        }

        case type === 'focus-layers': {
            this.$$boxes.forEach($box => $box.setAttribute(this.settings.hiddenAttr, ''));

            this.$$layers.forEach(($layer, index) => {
                const isLast = index === lastLayerIndex;

                // Focus all layers at the top while leaving only the last layer “active”.
                $layer.setAttribute(this.settings.focusedAttr, 'top');
                $layer.removeAttribute(this.settings.hiddenAttr);
                $layer.setAttribute(this.settings.inactiveAttr, !isLast);
                this.toggleVideoState($layer, null, isLast);
            });
            break;
        }

        case type.startsWith('center-focus-layer-'): {
            const targetIndex = parseInt(type.split('-').pop(), 10);
            this.$$boxes.forEach($box => $box.setAttribute(this.settings.hiddenAttr, ''));

            this.$$layers.forEach(($layer, index) => {
                const isTarget = index === targetIndex;

                // Only the target layer remains visible; others are hidden to emphasize the center focus.
                $layer.setAttribute(isTarget ? this.settings.focusedAttr : this.settings.hiddenAttr, 'center');
                $layer.setAttribute(this.settings.inactiveAttr, !isTarget);
                this.toggleVideoState($layer, null, isTarget);
            });
            break;
        }

        case type.startsWith('focus-layer-'): {
            const targetIndex = parseInt(type.split('-').pop(), 10);
            this.$$boxes.forEach($box => $box.removeAttribute(this.settings.hiddenAttr));

            this.$$layers.forEach(($layer, index) => {
                const isHidden = index < targetIndex && index !== lastLayerIndex;

                // Hide layers “above” the target (except the last), and deactivate non-target layers.
                $layer.removeAttribute(this.settings.focusedAttr);
                $layer.setAttribute(this.settings.inactiveAttr, index !== targetIndex && index !== lastLayerIndex);
                $layer.toggleAttribute(this.settings.hiddenAttr, isHidden);
                if (isHidden) {
                    $layer.setAttribute(this.settings.hiddenAttr, 'top');
                }

                // Play videos for visible layers only; hidden layers get paused.
                this.toggleVideoState($layer, null, !isHidden);
            });
            break;
        }
        }
    }

    /**
     * Enables or disables related content blocks based on the hovered/active hotspot.
     * This uses `aria-disabled` to hint assistive technologies about inactive content regions.
     *
     * @param {HTMLElement} $hotspot - The hotspot whose animation type determines the related toggle/content.
     * @param {boolean} active - Whether the hotspot is currently active (hovered/focused).
     * @returns {void} Does not return a value.
     */
    toggleContentState ($hotspot, active) {
        const animationType = $hotspot.getAttribute(this.settings.hotspotAttr);

        const $relatedToggle = Array.from(this.$$toggles).find($toggle =>
            $toggle.getAttribute(this.settings.toggleAttr) === animationType
        );

        this.$$toggles.forEach($toggle => {
            const $relatedContent = $toggle.closest(`[${this.settings.initAttr}="${this.settings.contentValue}"]`);

            // When a hotspot is active, disable all non-related content blocks to reduce distraction.
            if (!active) {
                $relatedContent?.removeAttribute('aria-disabled');
            } else if (active && $relatedToggle === $toggle) {
                $relatedContent?.removeAttribute('aria-disabled');
            } else {
                $relatedContent?.setAttribute('aria-disabled', true);
            }
        });
    }

    /**
     * Toggles the enabled/disabled state of hotspots depending on current animation state.
     * In non-initial states all hotspots are disabled; during initial state only the current
     * hotspot remains enabled when active, preventing accidental multi-selection.
     *
     * @param {HTMLElement} $currentHotspot - The hotspot currently being interacted with.
     * @param {boolean} active - Whether a hotspot interaction state is active.
     * @returns {void} Does not return a value.
     */
    toggleHotspotState ($currentHotspot, active) {
        this.$$hotspots.forEach($hotspot => {
            if (this.animationType !== 'initial') {
                $hotspot.setAttribute('disabled', '');
            } else {
                if (!active) {
                    $hotspot.removeAttribute('disabled');
                } else {
                    if ($currentHotspot === $hotspot) {
                        $hotspot.removeAttribute('disabled');
                    } else {
                        $hotspot.setAttribute('disabled', '');
                    }
                }
            }
        });
    }

    /**
     * Registers event listeners for hotspots, media container clicks, module-level media control,
     * and global toggle state changes.
     *
     * @returns {void} Does not return a value.
     */
    initializeInteractions () {
        this.$$hotspots.forEach($hotspot => {
            $hotspot.addEventListener('mouseenter', () => {
                this.toggleContentState($hotspot, true);
                this.toggleHotspotState($hotspot, true);
            });

            $hotspot.addEventListener('mouseleave', () => {
                this.toggleContentState($hotspot, false);
                this.toggleHotspotState($hotspot, false);
            });

            $hotspot.addEventListener('click', () => {
                const animationType = $hotspot.getAttribute(this.settings.hotspotAttr);

                const $relatedToggle = Array.from(this.$$toggles).find($toggle =>
                    $toggle.getAttribute(this.settings.toggleAttr) === animationType
                );

                // Keep toggle UI and animation state in sync.
                $relatedToggle?.buttonToggleAPI?.changeState(true);
                if ($relatedToggle) {
                    this.toggleAnimationState(animationType);
                }
            });
        });

        this.$mediaValue?.addEventListener('click', (e) => {
            const isActive = this.$root.hasAttribute(this.settings.activeAttr);
            const hotspots = Array.from(this.$$hotspots);

            // Clicking outside hotspots in the media region resets back to the initial view.
            if (!hotspots.includes(e.target) && this.animationType !== 'initial' && isActive) {
                this.toggleAnimationState('initial');

                // Reset all button toggles to their inactive state.
                this.$$toggles.forEach($toggle => {
                    if ($toggle.buttonToggleAPI) {
                        $toggle.buttonToggleAPI.changeState(false);
                    }
                });
            }
        });

        this.$root.addEventListener('media_control.change', (event) => {
            this.paused = event.detail.pressed;

            // Pause/resume all videos and reflect the paused state on the root element.
            if (this.paused) {
                this.$root.setAttribute(this.settings.pausedAttr, '');
                this.$$videos.forEach($video => {
                    $video.pause();
                });
            } else {
                this.$root.removeAttribute(this.settings.pausedAttr);
                this.$$videos.forEach($video => {
                    $video.play();
                });
            }
        });

        window.addEventListener('buttonToggle.stateChange', (e) => {
            const api = e.detail.api;
            const state = e.detail.state;

            // When one toggle changes, apply its animation and reset all others.
            this.$$toggles.forEach($toggle => {
                if ($toggle === api.$root) {
                    const animationType = $toggle.getAttribute(this.settings.toggleAttr);
                    this.toggleAnimationState(state ? animationType : 'initial');
                } else {
                    if ($toggle.buttonToggleAPI) {
                        $toggle.buttonToggleAPI.changeState(false);
                    }
                }
            });
        });
    }

    /**
     * Plays or pauses a layer video depending on `shouldPlay` and the global paused state.
     * If `$video` is `null`, the method will resolve the video element from `$layer`.
     *
     * @param {HTMLElement|null} $layer - Layer element used to locate the video when `$video` is `null`.
     * @param {HTMLVideoElement|null} $video - Video element to control; may be `null` to auto-resolve.
     * @param {boolean} shouldPlay - Whether the target video should play (subject to global pause).
     * @returns {void} Does not return a value.
     */
    toggleVideoState ($layer, $video, shouldPlay) {
        if ($video === null) {
            $video = $layer.querySelector(`[${this.settings.initAttr}="${this.settings.videoValue}"]`);
        }

        if (!$video) {
            return;
        }

        // Respect the module-level pause state: no video should play while paused.
        if (shouldPlay && !this.paused) {
            $video.play();
        } else {
            $video.pause();
        }
    }

    /**
     * Replays videos when the module re-enters the viewport.
     * If a video has buffered enough, its currentTime is reset to `0` before attempting playback.
     *
     * @returns {void} Does not return a value.
     */
    replayInView () {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.$$videos.forEach($video => {
                        // Reset playback to the beginning for already-initialized media.
                        if ($video.readyState > 1) {
                            $video.currentTime = 0;
                        }

                        // Keep behavior consistent with existing calls (arguments are intentionally unchanged).
                        this.toggleVideoState(true);
                    });
                }
            });
        }, {
            threshold: this.settings.threshold
        });

        observer.observe(this.$root);
    }
}

export { FocusLayer };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-focuslayer="root"]');
        $$roots.forEach($root => {
            const $rootAPI = new FocusLayer($root);
            $rootAPI.initialize();
        });
    }
});
